<script setup lang="ts">
import request from 'axios'
import type { LocationQuery } from 'vue-router'
import { PostContentsType, type PostsOrderBy, type PostsPayload } from '@store'
import { ROUTES } from '@configs'
import type { NovaPostOrderValue } from '@components/NovaPostOrder/NovaPostOrder.types'

const { gtEvent } = useGoogleTag()
const { show: modalShow, hide: modalHide } = useModal()
const { t, locale, messages } = useI18n()
const { openMembershipServicePopup } = useMembershipProcess()
const route = useRoute()
const pageFeedRef = ref<Element>()
const layoutStore = useLayoutStore()
const userStore = useUserStore()
const postStore = usePostStore()
const sendbirdStore = useSendbirdStore()
const isLoading = ref(false)
const isError = ref(false)
const { feedPosts, categories } = storeToRefs(postStore)
const isMounted = ref(false)

useSetSEO()
useScrollRestoration()

// 포스트 목록 조회 쿼리 비교
const checkEqualPostsPayload = (query?: LocationQuery) => {
  const _query = query || route.query
  const allowOrderBy: Array<PostsOrderBy> = orders.value.map(
    (item) => item.value
  )
  const newPayload = {
    orderBy:
      allowOrderBy.find((item) => item === String(_query.orderBy)) || 'for-you',
    cmtyNttCtgrySn:
      categories.value.find(
        (item) => item.cmtyNttCtgrySn === Number(_query.cmtyNttCtgrySn)
      )?.cmtyNttCtgrySn ||
      categories.value.find((item) => item.ctgryDfltCeckAt === 'Y')
        ?.cmtyNttCtgrySn ||
      categories.value[0].cmtyNttCtgrySn,
    flwUserSn: _query.flwUserSn ? Number(_query.flwUserSn) : undefined,
  }
  const isChanged =
    Object.keys(newPayload).filter(
      (key) =>
        newPayload[key as keyof typeof newPayload] !==
        feedPosts.value.payload[key]
    ).length > 0
  return { isChanged, newPayload }
}

const orders = computed(() =>
  Object.keys(messages.value[locale.value].sort).map((key) => ({
    value: key as PostsOrderBy,
    label: t(`sort.${key}`),
  }))
)

const activeOrder = computed(
  () =>
    orders.value.find((item) => item.value === feedPosts.value.payload.orderBy)
      ?.value || ''
)

const feedCategory = computed(() => {
  if (!categories.value || !feedPosts.value.payload.cmtyNttCtgrySn) {
    return
  }
  return categories.value.find(
    (item) => item.cmtyNttCtgrySn === feedPosts.value.payload.cmtyNttCtgrySn
  )
})

const feedCategories = computed(() =>
  categories.value.map((category) => ({
    id: category.cmtyNttCtgrySn,
    name: category.nttCtgryNm,
  }))
)

const activeFeedCategory = computed(() =>
  feedCategories.value.find(
    (category) => category.id === feedPosts.value.payload.cmtyNttCtgrySn
  )
)

watch(
  () => route.query,
  (cur) => {
    const { isChanged, newPayload } = checkEqualPostsPayload(cur)
    isChanged && fetchPosts(true, newPayload)
  },
  { deep: true }
)

// 포스트 정렬 변경
const handleOnChangeActiveSortItem = (value: NovaPostOrderValue) => {
  if (!userStore.isSignIn && value === 'follow-newest') {
    openMembershipServicePopup.signin()
    return
  }

  const orderName =
    orders.value.find((item) => item.value === value)?.label || ''

  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '포스트 정렬 방식 선택',
    eventLabel: orderName,
    eventSlot: `${feedCategory.value?.nttCtgryNm}`,
    eventI18nAddr: orderName,
    eventComponent: 'Button',
  })

  useNavigations({
    url: ROUTES.FEED.path,
    query: { ...route.query, orderBy: String(value) },
    type: 'replace',
  })
}

// 포스트 목록 카테고리 변경
const handleOnChangeFeedCategory = (
  category: typeof activeFeedCategory.value
) => {
  if (feedPosts.value.payload.cmtyNttCtgrySn === category.id) return
  if (feedPosts.value.loading) return

  useNavigations({
    url: ROUTES.FEED.path,
    type: 'replace',
    query: { ...route.query, cmtyNttCtgrySn: category.id },
  })
}

// 포스트 목록 조회
const fetchPosts = async (
  refresh = false,
  payload?: Partial<PostsPayload>,
  fetchLive = false
) => {
  try {
    // 1998 에러 테스트를 위한 코드
    // if (
    //   !refresh &&
    //   feedPosts.value.payload.orderBy === 'for-you' &&
    //   feedPosts.value.payload.pageNum > 5
    // ) {
    //   payload = { ...payload, nttRecomendStdrDt: 'ss' }
    // }

    if (refresh) {
      layoutStore.updateLoadingIndicatorGlobal({ show: true })
    }
    isLoading.value = true
    isError.value = false
    await postStore.fetchPosts(
      { ...feedPosts.value.payload, ...payload },
      refresh,
      'feed'
    )

    if (fetchLive) {
      await sendbirdStore.fetchOnAirLives()
    }

    if (!refresh) {
      gtEvent('scrollEvent', {
        eventCategory: '더보기',
        eventAction: '피드 더보기',
        eventLabel: 'loadMore',
        eventSlot: `${feedCategory.value?.nttCtgryNm}`,
        eventI18nAddr: 'loadMore',
        eventComponent: 'RecyclerView',
        pageNum: feedPosts.value.payload.pageNum,
        pageSize: feedPosts.value.payload.pageSize,
        nextPageSize: feedPosts.value.payload.nextPageSize,
        cmtyNttCtgrySn: feedPosts.value.payload.cmtyNttCtgrySn,
      })
    }

    // 1998 에러로 인해 재호출시 열려있던 안내 모달 닫기
    if (
      feedPosts.value.payload.orderBy === 'for-you' &&
      refresh &&
      layoutStore.modals.findIndex(
        (item) => item.modalNameKey === modalsName.MODAL_FEED_FOR_YOU_REFRESH
      ) !== -1
    ) {
      await useSleeper(1000)
      await modalHide(modalsName.MODAL_FEED_FOR_YOU_REFRESH)
    }
  } catch (err) {
    isError.value = true
    if (request.isAxiosError(err) && err.response?.status === 1998) {
      await modalShow(modalsName.MODAL_FEED_FOR_YOU_REFRESH)
      await fetchPosts(true)
    } else {
      useToast(t('commonError.network.message'))
    }
  } finally {
    if (refresh) {
      layoutStore.updateLoadingIndicatorGlobal({ show: false })
    }
    isLoading.value = false
  }
}

onMounted(async () => {
  isMounted.value = true
  const { isChanged, newPayload } = checkEqualPostsPayload()
  // 이미 포스트 리스트가 스토어에 있고 목록 조회 조건이 변경되지 않았다면 목록을 재조회 하지 않는다
  if (feedPosts.value.items.length && !isChanged) return
  await fetchPosts(true, newPayload)
  await sendbirdStore.fetchOnAirLives(true)
})
</script>

<template>
  <div ref="pageFeedRef" class="page-feed">
    <NovaBoxLiveList :bar-mode="true" />

    <div class="sorter-bar">
      <NovaPostOrder
        v-if="layoutStore.responsive !== 'mobile'"
        :active-order="activeOrder"
        :orders="orders"
        @on-change-active-order="handleOnChangeActiveSortItem"
      />

      <NovaBoxSorter
        v-else
        :sort-items="feedCategories"
        :active-sort-item="activeFeedCategory"
        @on-change-active-sort-item="handleOnChangeFeedCategory"
      >
        <template #tools>
          <NovaDropdown
            :theme="'dark'"
            :menus="orders"
            :active-value="activeOrder"
            @update="handleOnChangeActiveSortItem"
          />
        </template>
      </NovaBoxSorter>
    </div>

    <NovaBoxEmptyMessage
      v-if="feedPosts.refresh && !feedPosts.items.length && !isError"
      :message="$t('statusMessages.mypageList.post.empty')"
      :fill="true"
    />

    <NovaBoxEmptyMessage
      v-if="!feedPosts.items.length && isError"
      :message="$t('statusMessages.mypageList.post.error')"
      :fill="true"
    />

    <NovaRecyclerGrid
      v-if="feedPosts.items.length"
      :items="feedPosts.items"
      :page-mode="true"
      :key-name="'cmtyNttSn'"
      :is-last="feedPosts.last"
      :is-loading="isLoading"
      :is-error="isError"
      :is-refresh="feedPosts.refresh"
      :error-message="$t('statusMessages.mypageList.post.error')"
      :responsive="{
        mobile: {
          columns: 1,
          rowGap: 12,
          columnGap: 12,
          padding: { left: 10, right: 10 },
        },
        tablet: { columns: 1, rowGap: 12, columnGap: 12 },
        desktop: { columns: 1, rowGap: 12, columnGap: 12 },
      }"
      :buffer="600"
      @load-more="() => isMounted && fetchPosts(false)"
    >
      <template #item="{ item }">
        <NovaBoxPost
          :source="item"
          :view-type="PostContentsType.LIST"
          :list-view-height="'auto'"
          :open-detail-url="
            useRoutePathIdChange(ROUTES.FEED_DETAIL.path, {
              id: item.cmtyNttSn,
            })
          "
        />
      </template>
    </NovaRecyclerGrid>
  </div>
</template>

<style lang="scss" scoped>
.page-feed {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: clip;

  .sorter-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-top: 20px;
    padding-bottom: 20px;
    background: linear-gradient(
      180deg,
      hex-to-rgba($color-bg-2, 1) 80%,
      hex-to-rgba($color-bg-2, 0) 100%
    );
    z-index: 20;
    //overflow-x: clip;
    width: 100%;

    &:before,
    &:after {
      position: absolute;
      display: block;
      content: '';
      top: 0;
      width: 15px;
      height: 100%;
      background-color: $color-bg-2;
    }

    &:before {
      left: -15px;
    }

    &:after {
      right: -15px;
    }

    .btn-create-post {
      flex-shrink: 0;
    }
  }

  @include mobile {
    padding-top: 0;

    .sorter-bar {
      padding: 14px 14px 20px;
    }
  }
}
</style>
